import { type HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

export type BadgeTone = 'brand' | 'accent' | 'success' | 'warning' | 'danger' | 'neutral';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Show a small leading dot. Useful for status badges. */
  withDot?: boolean;
}

const TONE_CLASSES: Record<BadgeTone, string> = {
  brand: 'bg-brand-500/12 text-brand-600 dark:text-brand-300 ring-brand-500/25',
  accent: 'bg-accent-500/12 text-accent-600 dark:text-accent-300 ring-accent-500/25',
  success: 'bg-success-500/12 text-success-500 dark:text-success-400 ring-success-500/25',
  warning: 'bg-warning-500/12 text-warning-500 ring-warning-500/25',
  danger: 'bg-danger-500/12 text-danger-500 dark:text-danger-400 ring-danger-500/25',
  neutral: 'bg-[var(--surface-sunken)] text-[var(--text-secondary)] ring-[var(--border-subtle)]',
};

const DOT_CLASSES: Record<BadgeTone, string> = {
  brand: 'bg-brand-500',
  accent: 'bg-accent-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
  neutral: 'bg-[var(--text-muted)]',
};

export function Badge({
  tone = 'brand',
  withDot = false,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1',
        'text-xs font-medium ring-1 ring-inset',
        TONE_CLASSES[tone],
        className,
      )}
      {...rest}
    >
      {withDot ? (
        <span className={cn('size-1.5 rounded-full', DOT_CLASSES[tone])} aria-hidden="true" />
      ) : null}
      {children}
    </span>
  );
}
